import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import { getTokens } from "../../styles/tokens";

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    root: {
      display: "flex",
      alignItems: "flex-end",
      justifyContent: "space-between",
      flexWrap: "wrap",
      columnGap: 16,
      rowGap: 12,
      padding: "2px 0 22px",
      minWidth: 0,
      [theme.breakpoints.down("xs")]: {
        alignItems: "stretch",
        padding: "0 0 16px",
      },
    },
    left: {
      minWidth: 0,
      flex: "1 1 auto",
    },
    breadcrumb: {
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: 6,
      fontFamily: t.font,
      fontSize: 12,
      fontWeight: 500,
      color: t.textMuted,
      marginBottom: 6,
      "& a": {
        color: "inherit",
        textDecoration: "none",
        "&:hover": { color: t.textSecondary },
      },
    },
    sep: {
      opacity: 0.6,
      userSelect: "none",
    },
    titleRow: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      minWidth: 0,
    },
    title: {
      fontFamily: t.font,
      ...t.type.pageHeading,
      color: t.text,
      margin: 0,
      minWidth: 0,
      textWrap: "balance",
    },
    subtitle: {
      fontFamily: t.font,
      fontSize: 14,
      lineHeight: 1.5,
      color: t.textSecondary,
      margin: "6px 0 0",
      overflowWrap: "anywhere",
    },
    actions: {
      flex: "none",
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: 8,
      marginLeft: "auto",
      [theme.breakpoints.down("xs")]: {
        width: "100%",
        marginLeft: 0,
        justifyContent: "flex-start",
      },
    },
  };
});

/**
 * Cabeçalho de página no padrão do painel: título grande, subtítulo e ações à direita.
 *
 * Props: `title` (obrigatório), `subtitle?`, `actions?` (nó ou array de nós),
 * `breadcrumb?` (array de `{ label, href? }` ou strings, ou um nó React),
 * `badge?` (nó exibido ao lado do título, ex.: contador/StatusBadge).
 */
const PageHeader = ({ title, subtitle, actions, action, breadcrumb, badge, className, children }) => {
  const classes = useStyles();
  const acts = actions != null ? actions : action;

  const renderBreadcrumb = () => {
    if (!breadcrumb) return null;
    if (!Array.isArray(breadcrumb)) return <div className={classes.breadcrumb}>{breadcrumb}</div>;
    return (
      <nav className={classes.breadcrumb} aria-label="breadcrumb">
        {breadcrumb.map((item, i) => {
          const isLast = i === breadcrumb.length - 1;
          const label = typeof item === "string" ? item : item.label;
          const href = typeof item === "string" ? undefined : item.href;
          return (
            <React.Fragment key={i}>
              {href && !isLast ? <a href={href}>{label}</a> : <span>{label}</span>}
              {!isLast && <span className={classes.sep}>/</span>}
            </React.Fragment>
          );
        })}
      </nav>
    );
  };

  return (
    <div className={[classes.root, "kp-page-header", className].filter(Boolean).join(" ")}>
      <div className={classes.left}>
        {renderBreadcrumb()}
        <div className={classes.titleRow}>
          <h1 className={`${classes.title} kp-page-title`}>{title}</h1>
          {badge}
        </div>
        {subtitle && <p className={classes.subtitle}>{subtitle}</p>}
        {children}
      </div>
      {acts && <div className={`${classes.actions} kp-page-header-actions`}>{acts}</div>}
    </div>
  );
};

export default PageHeader;
